import React from 'react';
import {
    Edit,
    SimpleForm,
    Toolbar,
    SaveButton,
    TextInput,
    DateInput,
    SelectInput,
    AutocompleteInput,
    FormDataConsumer,
    useRecordContext,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, Typography } from '@mui/material';
import EmployeeDocumentsSection from './EmployeeDocumentsSection';
import { xoaDau } from './utils/xoaDau';
import banksData from './banks.json';

/**
 * Custom form toolbar that renders only the Save button, omitting the default
 * react-admin Delete button.  Employee records must not be deleted; use the
 * Terminate workflow instead.
 */
const NoDeleteToolbar: React.FC = () => (
    <Toolbar>
        <SaveButton />
    </Toolbar>
);

/**
 * A section wrapper that renders a bold heading above a group of form inputs.
 *
 * Used to visually cluster related inputs on the employee edit page
 * so that related fields are presented near each other, improving usability.
 *
 * @param props.title - The section heading text.
 * @param props.children - The input components to render inside the section.
 */
const InputGroup: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <Box sx={{ mb: 3, width: '100%' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, borderBottom: '1px solid', borderColor: 'divider', pb: 0.5 }}>
            {title}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {children}
        </Box>
    </Box>
);

/**
 * Calculates years of service from a join date.
 * Returns the number of full years between the join date and today.
 */
function calculateYearsOfService (joinDate: string): number {
    const join = new Date(joinDate);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - join.getTime());
    const diffYears = diffTime / (365.25 * 24 * 60 * 60 * 1000);
    return Math.floor(diffYears);
}

/** Allowed gender values matching the DB convention. */
const GENDER_CHOICES = [
    { id: 'Male', name: 'Male' },
    { id: 'Female', name: 'Female' },
    { id: 'Other', name: 'Other' },
];

/** Allowed contract-type values matching the DB convention. */
const CONTRACT_TYPE_CHOICES = [
    { id: 'Unlimited', name: 'Unlimited' },
    { id: '1-year', name: '1-year' },
    { id: 'Probation', name: 'Probation' },
];

/** Title choices with Vietnamese-English mapping. */
const TITLE_CHOICES = [
    { id: 'Giám đốc', name: 'Giám đốc', en: 'Director' },
    { id: 'Phó giám đốc', name: 'Phó giám đốc', en: 'Vice Director' },
    { id: 'Trưởng phòng', name: 'Trưởng phòng', en: 'Head of Department' },
    { id: 'Phó trưởng phòng', name: 'Phó trưởng phòng', en: 'Deputy Head of Department' },
    { id: 'Kỹ sư', name: 'Kỹ sư', en: 'Engineer' },
    { id: 'Kỹ sư cao cấp', name: 'Kỹ sư cao cấp', en: 'Senior Engineer' },
    { id: 'Kỹ sư chính', name: 'Kỹ sư chính', en: 'Principal Engineer' },
    { id: 'Nhân viên', name: 'Nhân viên', en: 'Staff' },
    { id: 'Nhân viên cao cấp', name: 'Nhân viên cao cấp', en: 'Senior Staff' },
    { id: 'Thực tập sinh', name: 'Thực tập sinh', en: 'Intern' },
    { id: 'Tester', name: 'Tester', en: 'Tester' },
    { id: 'Tester cao cấp', name: 'Tester cao cấp', en: 'Senior Tester' },
    { id: 'Quản lý dự án', name: 'Quản lý dự án', en: 'Project Manager' },
    { id: 'Trưởng nhóm', name: 'Trưởng nhóm', en: 'Team Leader' },
    { id: 'Chuyên viên', name: 'Chuyên viên', en: 'Specialist' },
    { id: 'Chuyên viên cao cấp', name: 'Chuyên viên cao cấp', en: 'Senior Specialist' },
];

/**
 * Autocomplete choices for `bank_name`, built from the Vietnamese bank list
 * (`banks.json`, sourced from the Zalo BIN-code list). `id` is the bank's full
 * Vietnamese name (the value persisted to `bank_name`); `name` combines the
 * common short name with the full name (e.g. "Vietcombank - Ngân hàng TMCP
 * Ngoại Thương Việt Nam") so `AutocompleteInput`'s default substring filtering
 * lets admins quick-search using whichever form they remember.
 */
const BANK_CHOICES = (banksData as Array<{ bin: string; code: string; shortName: string; name: string }>)
    .map(bank => ({
        id: bank.name,
        name: `${bank.shortName} - ${bank.name}`,
    }));

/**
 * Default `bank_name` value applied when an employee record has none set yet
 * (e.g. records created before Banking Information was tracked). Resolved
 * from {@link BANK_CHOICES} by BIDV's bank code, so it stays in sync with
 * `banks.json` rather than duplicating BIDV's full Vietnamese name here.
 */
const DEFAULT_BANK_NAME = (banksData as Array<{ code: string; name: string }>)
    .find(bank => bank.code === 'BIDV')?.name ?? 'Ngân hàng TMCP Đầu tư và Phát triển Việt Nam';

/** Common ID issue places in Vietnam. */
const ID_ISSUE_PLACE_CHOICES = [
    { id: 'Cục cảnh sát quản lý hành chính về trật tự xã hội', name: 'Cục cảnh sát quản lý hành chính về trật tự xã hội' },
    { id: 'Cục Cảnh sát quản lý hành chính về trật tự xã hội - Bộ Công an', name: 'Cục Cảnh sát quản lý hành chính về trật tự xã hội - Bộ Công an' },
    { id: 'Công an tỉnh', name: 'Công an tỉnh' },
    { id: 'Công an thành phố', name: 'Công an thành phố' },
    { id: 'Công an quận', name: 'Công an quận' },
    { id: 'Công an huyện', name: 'Công an huyện' },
    { id: 'Phòng Cảnh sát ĐKHL về trật tự xã hội Công an tỉnh/TP', name: 'Phòng Cảnh sát ĐKHL về trật tự xã hội Công an tỉnh/TP' },
];

/**
 * Side-effect component that auto-derives `english_name` from `full_name`.
 *
 * Whenever `full_name` changes, the component applies {@link xoaDau} and writes
 * the result into `english_name` — but only when `english_name` still matches
 * the previously auto-derived value (i.e. the user has not manually overridden
 * it).  A manual override is preserved on all subsequent `full_name` changes.
 *
 * Renders nothing; it exists solely for its side-effect.
 */
const EnglishNameAutofill: React.FC = () => {
    const { setValue } = useFormContext();
    const fullName: string = (useWatch({ name: 'full_name' }) as string) ?? '';
    const englishName: string = (useWatch({ name: 'english_name' }) as string) ?? '';

    const lastAutoRef = React.useRef<string>(xoaDau(fullName));
    const prevFullRef = React.useRef<string>(fullName);

    React.useEffect(() => {
        if (fullName === prevFullRef.current) return;
        prevFullRef.current = fullName;

        if (englishName === lastAutoRef.current) {
            const derived = xoaDau(fullName);
            lastAutoRef.current = derived;
            setValue('english_name', derived, { shouldDirty: true });
        }
    }, [fullName, englishName, setValue]);

    return null;
};

/**
 * Side-effect component that auto-sets `title_en` from selected `title`.
 *
 * When user selects a Vietnamese title from the dropdown, this component
 * finds the corresponding English title from {@link TITLE_CHOICES} and
 * writes it into `title_en`.
 *
 * Renders nothing; it exists solely for its side-effect.
 */
const TitleEnAutofill: React.FC = () => {
    const { setValue } = useFormContext();
    const title: string = (useWatch({ name: 'title' }) as string) ?? '';
    const prevTitleRef = React.useRef<string>(title);

    React.useEffect(() => {
        if (title === prevTitleRef.current) return;
        prevTitleRef.current = title;

        const choice = TITLE_CHOICES.find(c => c.id === title);
        if (choice) {
            setValue('title_en', choice.en, { shouldDirty: true });
        }
    }, [title, setValue]);

    return null;
};

/** Default value for id_issue_place when null */
const DEFAULT_ID_ISSUE_PLACE = 'Cục cảnh sát quản lý hành chính về trật tự xã hội';

/**
 * Transform record data before entering the form.
 * Converts null id_issue_place to default value.
 * Converts null/missing bank_name to the default BIDV entry.
 */
const transformRecord = (data: Record<string, unknown>) => ({
    ...data,
    id_issue_place: data.id_issue_place ?? DEFAULT_ID_ISSUE_PLACE,
    bank_name: data.bank_name ?? DEFAULT_BANK_NAME,
});

/**
 * Inner form component rendered inside `<Edit>` that can access the record context.
 *
 * Separated from `EmployeeEdit` so that `useRecordContext` can be
 * called after the record is loaded by the `<Edit>` wrapper.
 */
const EmployeeForm: React.FC = () => {
    const record = useRecordContext<{ id: number }>();

    return (
        <SimpleForm toolbar={<NoDeleteToolbar />}>
            {/* ── Identifier (read-only context) ───────────────────── */}
            <Box sx={{ mb: 2, width: '100%', display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextInput source="employee_id" label="Employee ID" disabled />
                <TextInput source="email" label="Email" disabled />
            </Box>

            {/* ── Personal Information ─────────────────────────────── */}
            <InputGroup title="Personal Information">
                <TextInput source="full_name" label="Full Name (Vietnamese)" />
                {/* Auto-fill side-effect: updates english_name whenever full_name changes */}
                <EnglishNameAutofill />
                <TextInput source="english_name" label="English Name" />
                <SelectInput source="gender" label="Gender" choices={GENDER_CHOICES} />
                <DateInput source="dob" label="Date of Birth" />
                <TextInput source="phone" label="Phone" />
            </InputGroup>

            {/* ── Employment Details ───────────────────────────────── */}
            <InputGroup title="Employment Details">
                <TextInput source="department" label="Department" />
                <FormDataConsumer>
                    {({ formData }) => {
                        const currentValue = formData?.title;
                        const dynamicChoices = currentValue && !TITLE_CHOICES.find(c => c.id === currentValue)
                            ? [...TITLE_CHOICES, { id: currentValue, name: currentValue, en: currentValue }]
                            : TITLE_CHOICES;
                        return (
                            <AutocompleteInput
                                source="title"
                                label="Title (Vietnamese)"
                                choices={dynamicChoices}
                            />
                        );
                    }}
                </FormDataConsumer>
                <TitleEnAutofill />
                <TextInput source="title_en" label="Title (English)" />
                <SelectInput source="contract_type" label="Contract Type" choices={CONTRACT_TYPE_CHOICES} />
                <DateInput source="join_date" label="Join Date" />
                <DateInput source="official_contract_date" label="Official Contract Date" />
                <DateInput source="termination_date" label="Termination Date" helperText="Clear to reactivate the employee" />
                <FormDataConsumer>
                    {({ formData }) => {
                        const joinDate = formData?.join_date;
                        if (joinDate) {
                            const years = calculateYearsOfService(joinDate);
                            return (
                                <Box sx={{ mt: 1, mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Years of Service: <strong>{years}</strong> (calculated from join date)
                                    </Typography>
                                </Box>
                            );
                        }
                        return null;
                    }}
                </FormDataConsumer>
                <TextInput source="level" label="Level" />
            </InputGroup>

            {/* ── Identification & Address ─────────────────────────── */}
            <InputGroup title="Identification & Address">
                <TextInput source="id_number" label="ID Number" />
                <DateInput source="id_issue_date" label="ID Issue Date" />
                <FormDataConsumer>
                    {({ formData }) => {
                        const currentValue = formData?.id_issue_place;
                        const dynamicChoices = currentValue && !ID_ISSUE_PLACE_CHOICES.find(c => c.id === currentValue)
                            ? [...ID_ISSUE_PLACE_CHOICES, { id: currentValue, name: currentValue }]
                            : ID_ISSUE_PLACE_CHOICES;
                        return (
                            <AutocompleteInput
                                source="id_issue_place"
                                label="ID Issue Place"
                                choices={dynamicChoices}
                            />
                        );
                    }}
                </FormDataConsumer>
                <TextInput source="address" label="Address" fullWidth />
            </InputGroup>

            {/* ── Banking Information ──────────────────────────────── */}
            <InputGroup title="Banking Information">
                <FormDataConsumer>
                    {({ formData }) => {
                        const currentValue = formData?.bank_name;
                        const dynamicChoices = currentValue && !BANK_CHOICES.find(c => c.id === currentValue)
                            ? [...BANK_CHOICES, { id: currentValue, name: currentValue }]
                            : BANK_CHOICES;
                        return (
                            <AutocompleteInput
                                source="bank_name"
                                label="Bank Name"
                                choices={dynamicChoices}
                            />
                        );
                    }}
                </FormDataConsumer>
                <TextInput
                    source="account_number"
                    label="Account Number"
                    parse={(value: string) => (typeof value === 'string' ? value.trim() : value)}
                />
            </InputGroup>

            {/* ── Notes ────────────────────────────────────────────── */}
            <InputGroup title="Notes">
                <TextInput source="note" label="Note" multiline rows={3} fullWidth />
            </InputGroup>

            {/* ── Documents ────────────────────────────────────────── */}
            <InputGroup title="Documents">
                {record?.id && (
                    <EmployeeDocumentsSection accountingId={record.id} />
                )}
            </InputGroup>
        </SimpleForm>
    );
};

/**
 * Edit form for a single `employees` record (employment accounting data).
 *
 * Read-only identifier fields (`employee_id`, `email`) are shown as disabled
 * inputs to provide context without allowing accidental changes.
 * All other fields are editable.
 *
 * Fields are grouped by category so that related inputs are presented near each
 * other, matching the layout of {@link EmployeeShow}:
 *
 * **Personal Information**, **Employment Details**, **Identification & Address**,
 * **Banking Information**, **Notes**, **Documents** (five fixed upload slots)
 *
 * @returns A react-admin `<Edit>` component with a grouped `<SimpleForm>`.
 */
export const EmployeeEdit: React.FC = () => (
    <Edit transform={transformRecord}>
        <EmployeeForm />
    </Edit>
);
